import React, { useCallback, useState } from "react";
import { Button } from "components/ui/button";
import { Copy, Check } from "lucide-react";

async function copyToClipboard(text) {
  // Modern API
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  // Fallback (older browsers / some insecure contexts)
  const el = document.createElement("textarea");
  el.value = text;
  el.setAttribute("readonly", "");
  el.style.position = "fixed";
  el.style.left = "-9999px";
  document.body.appendChild(el);
  el.select();
  document.execCommand("copy");
  document.body.removeChild(el);
}

export default function CopyText({
  value, // string | () => string
  children,
  className = "",
  buttonClassName = "",
  label = "העתק",
  copiedLabel = "הועתק",
  disabled = false,
  onCopied,
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      const text = typeof value === "function" ? value() : value;
      const normalized = String(text ?? "").trim();
      if (!normalized) return;

      await copyToClipboard(normalized);
      setCopied(true);
      onCopied?.(normalized);
      window.setTimeout(() => setCopied(false), 900);
    } catch (e) {
      console.error("Copy failed:", e);
    }
  }, [value, onCopied]);

  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      <span className="min-w-0">{children}</span>

      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={`h-7 w-7 shrink-0 ${buttonClassName}`}
        onClick={handleCopy}
        disabled={disabled}
        aria-label={copied ? copiedLabel : label}
        title={copied ? copiedLabel : label}
      >
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </Button>
    </span>
  );
}
